import { useState, useEffect } from 'react'
import {
  Button, ModalContainer,
  ModalContent, CloseModal,
  ButtonContainer, MissionApprovalContainer, Review,
  ReviewInfo, Name, CreatedAtDate, Content, TextArea, TextContainer,
  InputName, ButtonSubmit, ButtonBox, Spinner
} from './styles'
import WidgetIcons from '../../utils/icons'
import { makeMissionReviews, createReview } from '../../../main/usecases/mission-reviews/missions-factory'
import { t } from '../../adapters/translate/translate'
import { getLocalStorage, addLocalStorage } from '../../utils/storage_helpers'
import { useGame, useTour } from '../../contexts'
import { GameState } from '../../../domain/enum/game-state.enum'
import { TriggerType } from '../../../domain/enum/trigger-type.enum'

type Props = {
  isShowing?: boolean;
  color?: string;
  mission: any;
  onAction: () => void;
  approved: boolean;
  topButtonContainer?: string;
  topModalContainer?: number;
  isMissionTrigger?: boolean;
  missionTrigger?: any;
  openModal: boolean;
  setOpenModal: any;
}

const MissionApproval = ({
  color, mission,
  isShowing = true, onAction,
  approved, topButtonContainer = '15px', topModalContainer = 5,
  isMissionTrigger = false,
  missionTrigger,
  openModal,
  setOpenModal
}: Props) => {
  const [openModalName, setOpenModalName] = useState(false)
  const [tryApprove, setTryApprove] = useState(false)
  const [reviews, setReviews] = useState<any>()
  const [comment, setComment] = useState(null)
  const [missionTriggerName, setMissionTriggerName] = useState(null)
  const [name, setName] = useState('')
  const [isLoading, setIsloading] = useState(false)
  const [missionData, setMissionData] = useState<any>(null)
  const { handleToggleTour, tourWasCompleted, showInstructions, currentStep } = useTour()
  const { game } = useGame()

  async function handleOpenReviews () {
    const res = await makeMissionReviews().load(missionData.uuid)
    setReviews(res['hydra:member'].length !== 0 ? res['hydra:member'] : false)
    setOpenModal(true)
  }

  async function missionApproved () {
    if (!getLocalStorage('nameForApprovement')) {
      setOpenModalName(true)
      setTryApprove(true)
    } else {
      try {
        setIsloading(true)
        const res = await createReview().post({
          name: getLocalStorage('nameForApprovement'),
          feedback: isMissionTrigger ? `${missionTriggerName} da Missão: Aprovada por ${getLocalStorage('nameForApprovement')}` : 'Missão aprovada por ' + getLocalStorage('nameForApprovement'),
          status: 'approved',
          mission: missionData.uuid
        })
        if (reviews) {
          setReviews([...reviews, res])
        } else {
          setReviews([res])
        }
        onAction()
        setIsloading(false)
      } catch (error) {
        console.error('MissionReview.sendReview', error)
      }
    }
  }

  async function sendReview () {
    if (!getLocalStorage('nameForApprovement')) {
      setOpenModalName(true)
    } else {
      if (comment) {
        try {
          const res = await createReview().post({
            name: getLocalStorage('nameForApprovement'),
            feedback: isMissionTrigger ? `${missionTriggerName} da Missão: ${comment}` : comment,
            status: 'pending',
            mission: missionData.uuid
          })
          onAction()
          setComment(null)
          if (reviews) {
            setReviews([...reviews, res])
          } else {
            setReviews([res])
          }
        } catch (error) {
          console.error('MissionReview.sendReview', error)
        }
      }
    }
  }

  const handleChange = (event: any) => {
    if (event.target.id === 'TextComment') {
      setComment(event.target.value)
    }
    if (event.target.id === 'NameComment') {
      setName(event.target.value)
    }
  }

  const saveName = async () => {
    addLocalStorage('nameForApprovement', name)
    setOpenModalName(false)
    if (tryApprove) {
      await missionApproved()
    } else {
      await sendReview()
    }
  }

  useEffect(() => {
    if (game.gameState === GameState.APPROVAL) {
      const alreadyGoToGreetingsPage = getLocalStorage('greetings') || null
      if (!tourWasCompleted && showInstructions && alreadyGoToGreetingsPage) {
        handleToggleTour()
      }
    }
  }, [tourWasCompleted, showInstructions, game])

  const createMissionTriggerName = (triggerType: TriggerType) => {
    switch (triggerType) {
      case TriggerType.ATTEMPTS: return t('ATTEMPTS_TRIGGER_NAME')
      case TriggerType.CORRECT_ANSWER: return t('CORRECT_ANSWER_TRIGGER_NAME')
      case TriggerType.TIMEOUT: return t('TIMEOUT_TRIGGER_NAME')
      case TriggerType.TRANSITION: return t('TRANSITION_TRIGGER_NAME')
      case TriggerType.WRONG_ANSWER: return t('WRONG_ANSWER_TRIGGER_NAME')
      default: return t('DEFAULT_TRIGGER_NAME')
    }
  }

  useEffect(() => {
    if (isMissionTrigger) {
      setMissionTriggerName(createMissionTriggerName(missionTrigger.triggerType))
    }
  }, [missionTrigger])

  useEffect(() => {
    if (mission !== undefined) {
      setMissionData(JSON.parse(String(getLocalStorage('currentMission'))))
    }
  }, [mission])

  return (
    <MissionApprovalContainer>
      <ButtonContainer positionTop={topButtonContainer}>
        <Button
          isShowing={isShowing}
          onClick={handleOpenReviews}
          color={color}
          clicked={false}
          className={
            (currentStep.className === 'tourApproval-1' && !tourWasCompleted
              ? 'tourApproval-1 '
              : ''
            )}>
            <WidgetIcons name='RiEdit2Fill' size={25} color='#F24822' />
        </Button>

        <Button
          isShowing={isShowing}
          onClick={missionApproved}
          color={color}
          clicked={approved}
          className={
            (currentStep.className === 'tourApproval-2' && !tourWasCompleted
              ? 'tourApproval-2 '
              : ''
            )}>
          { isLoading ? <Spinner color={color} /> : <WidgetIcons name='RiCheckFill' size={35} color={approved ? '#fff' : '#11A007'} />}
        </Button>
      </ButtonContainer>

      {openModal &&
        <ModalContainer positionTop={topModalContainer} isMissionTrigger={isMissionTrigger}>
          <CloseModal onClick={() => setOpenModal(false)}><WidgetIcons name='RiCloseCircleFill' size={35} color={'#444'} /></CloseModal>

          <ModalContent>
              <div className='title'>
                <h2>{reviews ? t('Comments') : t('Enter here your improvement suggestion or comment.')}</h2>
              </div>
            {reviews
              ? reviews.map((item: any, idx: number) => (
                <Review key={idx} className={idx % 2 === 0 ? 'align-left' : 'align-right'}>
                  <ReviewInfo>
                      <Name className={idx % 2 === 0 ? 'text-left' : 'text-right'}>{item.name} - <b>{t(item.status)}</b></Name>
                      <CreatedAtDate>{item.reviewDate && item.reviewDate.length === 5 ? item.reviewDate + ' ' + t('Today') : item.reviewDate}</CreatedAtDate>
                      <CreatedAtDate>{!item.reviewDate && t('Now')}</CreatedAtDate>

                  </ReviewInfo>
                  <Content>{item.feedback}</Content>
                  { idx + 1 === reviews.length &&
                    <TextContainer className={idx % 2 === 0 ? 'align-right' : 'align-left'}>
                      <TextArea id='TextComment' placeholder={t('Reply or add a comment...')} onChange={handleChange}></TextArea>
                      <ButtonBox><ButtonSubmit onClick={sendReview}>{t('Send')}</ButtonSubmit></ButtonBox>
                    </TextContainer>}
                </Review>))
              : <TextContainer>
                  <TextArea id='TextComment' placeholder={t('Leave a comment...')} onChange={handleChange}></TextArea>
                  <ButtonBox><ButtonSubmit onClick={sendReview}>{t('Send')}</ButtonSubmit></ButtonBox>
                </TextContainer>
                }

          </ModalContent>
        </ModalContainer>}
      {openModalName &&
        <ModalContainer positionTop={topModalContainer} isMissionTrigger={isMissionTrigger}>
          <CloseModal onClick={() => setOpenModalName(false)}><WidgetIcons name='RiCloseCircleFill' size={35} color={'#444'} /></CloseModal>
          <ModalContent>
            <div className='title'>
                <h2>{ t('Enter your name please')}</h2>
              </div>
            <TextContainer>
                <InputName id='NameComment' placeholder={t('Name')} onChange={handleChange}></InputName>
                <ButtonBox><ButtonSubmit onClick={saveName}>{t('Save')}</ButtonSubmit></ButtonBox>
            </TextContainer>
          </ModalContent>
        </ModalContainer>}

    </MissionApprovalContainer>
  )
}
export default MissionApproval
